import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Container,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Stack,
  Typography,
} from '@mui/material';

import Iconify from '../components/iconify';
import UploadButton from '../components/UploadButton';
import {
  createBlobInContainer,
  deleteBlobIfItExists,
  getBlobsInContainer,
} from 'src/api/filesApi';
import { formatFileName } from 'src/utils/azureBlob';

export default function BannersPage() {
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<{ blobUrl: string; blobName: string }[]>(
    []
  );

  useEffect(() => {
    const fetchImages = async () => {
      await getContainerImages();
    };

    fetchImages().catch(console.error);
  }, []);

  const getContainerImages = async () => {
    const data = await getBlobsInContainer('banners/');
    setImages(data);
  };

  const deleteImageHandler = async (value: any) => {
    await deleteBlobIfItExists(value);
    await getContainerImages();
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setLoading(true);
      const inputEl = event.target;
      const fileList = inputEl?.files;

      if (!fileList || fileList.length === 0) return;

      const uploadPromises: Promise<string>[] = [];
      const uploadedFiles: File[] = [];

      for (let i = 0; i < fileList.length; i++) {
        const originalFile = fileList[i];
        const fileName = formatFileName(originalFile.name);

        const file = new File([originalFile], fileName, {
          type: originalFile.type,
          lastModified: originalFile.lastModified,
        });

        uploadedFiles.push(file);

        // createBlobInContainer expects a prefix (directory) and appends file.name
        uploadPromises.push(createBlobInContainer(file, 'banners/'));
      }

      await Promise.all(uploadPromises);

      await getContainerImages();
    } catch (error) {
      console.error('File upload error:', error);
    } finally {
      setLoading(false);
      // Reset input value to allow re-selecting the same file
      if (event?.target) {
        try {
          event.target.value = '';
        } catch (_) {
          /* noop */
        }
      }
    }
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    target.value = '';
  };

  return (
    <>
      <Helmet>
        <title> Banners </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Banners
          </Typography>
          <UploadButton onUpload={handleUpload} onClick={handleInputClick} />
        </Stack>

        <Card sx={{ p: 2 }}>
          {images === null || loading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ImageList cols={2} rowHeight={264}>
              {images.map((item, index) => (
                <ImageListItem key={index}>
                  <img
                    src={`${item.blobUrl}?date=${new Date().getTime()}`}
                    srcSet={`${item.blobUrl}?date=${new Date().getTime()}`}
                    alt='library'
                    loading='lazy'
                  />
                  <ImageListItemBar
                    title={item.blobName}
                    subtitle={''}
                    actionIcon={
                      <IconButton
                        sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                        value={item.blobName}
                        onClick={() => {
                          deleteImageHandler(item.blobName);
                        }}
                      >
                        <Iconify icon='eva:trash-2-outline' />
                      </IconButton>
                    }
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Card>
      </Container>
    </>
  );
}
